import { useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ApiError,
  assignTask,
  fetchMembers,
  fetchLastSeen,
  fetchRoleAssignments,
  fetchTasks,
} from '../api/client';
import { useSession } from '../app/WorkforceContext';
import { useAsync } from '../app/useAsync';
import { formatDateInZone } from '../lib/format';
import { hasAnyPermission } from '../lib/navigation';
import { ASAL_KELOLA_TUGAS } from '../lib/task-back-link';
import { FIELD_TASK_STATUSES, formatTaskStatus } from '../lib/task-actions';
import { formatPriority, sortTasks } from '../lib/task-events';
import { createIdempotencyKeyHolder } from '../lib/idempotency';
import { kurirTersedia } from '../lib/worker-candidates';
import { keteranganTerakhir, petaTerakhir } from '../lib/last-seen';
import { EmptyState, ErrorCard, LoadingState } from '../states';

/**
 * Daftar tugas untuk dispatcher.
 *
 * Cakupannya ditentukan SERVER (`self`/`team`/`organization`) — halaman ini tidak mengirim
 * filter pengguna apa pun. Membuka URL-nya tanpa wewenang menghasilkan daftar kosong atau
 * penolakan, bukan tugas milik orang lain.
 */
export function TaskManagePage(): ReactNode {
  const session = useSession();
  const timezone = session.policy?.timezone ?? 'Asia/Jakarta';
  /*
   * Saringan awal dari `?status=` (#1758) — tautan "N tugas draf belum ditugaskan" di Beranda
   * membuka daftar yang sudah tersaring. Nilai yang tidak dikenal diabaikan: "Semua".
   */
  const [params] = useSearchParams();
  const [status, setStatus] = useState(() => {
    const diminta = params.get('status') ?? '';
    return (FIELD_TASK_STATUSES as readonly string[]).includes(diminta) ? diminta : '';
  });

  const keys = useMemo(() => createIdempotencyKeyHolder(), []);
  const [menugaskan, setMenugaskan] = useState<string | null>(null);
  const [gagal, setGagal] = useState<ApiError | null>(null);

  /*
   * Tugas, orang, dan peran ditarik BERSAMA — supaya penugasan dapat dilakukan di baris ini juga.
   *
   * Arahan Owner 26 Agustus 2026: "cukup dipilih saat kelola tugas". Sebelumnya menugaskan
   * menuntut berpindah ke halaman tersendiri yang isinya hanya daftar nama; satu keputusan kecil
   * dibayar dengan satu perpindahan layar, dan dispatcher yang menugaskan sepuluh tugas berpindah
   * dua puluh kali.
   */
  const { state, reload } = useAsync(async () => {
    const [tasks, members, roles, terakhir] = await Promise.all([
      fetchTasks(session.auth, status || undefined),
      fetchMembers(session.auth).catch(() => []),
      fetchRoleAssignments(session.auth).catch(() => []),
      /* Keterangan lokasi bersifat MEMBANTU, bukan menentukan: gagal memuatnya tidak boleh
         menghalangi penugasan, jadi daftarnya tetap tampil tanpa keterangan. */
      fetchLastSeen(session.auth).catch(() => []),
    ]);
    return {
      tasks,
      kurir: kurirTersedia(members, roles),
      terakhir: petaTerakhir(terakhir),
    };
  }, [session.auth, status]);

  /*
   * Waktu acuan dipotret sekali per muat, bukan dibaca ulang tiap render: keterangan umur yang
   * bergeser saat komponen render ulang membuat daftar tampak berkedip tanpa sebab.
   *
   * Di ATAS seluruh return bersyarat — aturan hook.
   */
  const sekarang = useMemo(() => new Date(), [state.status]);

  const canCreate = hasAnyPermission(session.me.permissions, ['workforce.task.create']);

  return (
    <>
      <section className="card">
        <div className="row space-between">
          <h2>Daftar tugas</h2>
          {canCreate ? <Link to="/tasks/new">Buat tugas</Link> : null}
        </div>
        <label htmlFor="status">Status</label>
        <select id="status" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Semua</option>
          {FIELD_TASK_STATUSES.map((s) => (
            <option key={s} value={s}>
              {formatTaskStatus(s)}
            </option>
          ))}
        </select>
      </section>

      {state.status === 'loading' ? <LoadingState label="Memuat daftar…" /> : null}
      {state.status === 'error' ? <ErrorCard error={state.error} onRetry={reload} /> : null}

      {state.status === 'ready' && state.data.tasks.length === 0 ? (
        <EmptyState
          title="Tidak ada tugas"
          hint={
            canCreate
              ? 'Buat tugas pertama untuk mulai menugaskan pekerjaan lapangan.'
              : 'Belum ada tugas dalam cakupan Anda.'
          }
        />
      ) : null}

      {state.status === 'ready' && state.data.tasks.length > 0 ? (
        <ul className="list">
          {sortTasks(state.data.tasks).map((task) => (
            <li key={task.id} className="card list-item">
              <div className="row space-between">
                <Link to={`/tasks/${task.id}`} state={{ asal: ASAL_KELOLA_TUGAS }}>
                  <strong>{task.title}</strong>
                </Link>
                <span className={`badge status-${task.status}`}>
                  {formatTaskStatus(task.status)}
                </span>
              </div>
              <p className="muted small">
                {task.taskNumber} · {formatPriority(task.priority)} ·{' '}
                {task.assigneeUserId ? 'sudah ditugaskan' : 'belum ada pelaksana'}
              </p>
              {task.scheduledWindowStart ? (
                <p className="muted small">
                  Jadwal {formatDateInZone(task.scheduledWindowStart, timezone)}
                </p>
              ) : null}
              {/*
               * Penugasan DI BARIS INI. Hanya untuk tugas yang belum punya pelaksana; yang sudah
               * ditugaskan diubah dari layar detail, karena mengalihkan pekerjaan orang lain
               * bukan tindakan sekilas yang pantas berada di daftar.
               */}
              {task.assigneeUserId === null ? (
                <select
                  aria-label={`Tugaskan ${task.title}`}
                  value=""
                  disabled={menugaskan !== null}
                  onChange={(e) => {
                    const userId = e.target.value;
                    if (userId.length === 0) return;
                    setMenugaskan(task.id);
                    setGagal(null);
                    void assignTask(session.auth, task.id, keys.current(), userId)
                      .then(() => {
                        keys.rotate();
                        setMenugaskan(null);
                        reload();
                      })
                      .catch((caught: unknown) => {
                        setGagal(
                          caught instanceof ApiError
                            ? caught
                            : new ApiError(0, 'UNKNOWN', 'Kesalahan tidak dikenal.', null),
                        );
                        setMenugaskan(null);
                      });
                  }}
                >
                  <option value="">
                    {menugaskan === task.id ? 'Menugaskan…' : 'Tugaskan kurir…'}
                  </option>
                  {state.data.kurir.map((m) => (
                    <option key={m.userId} value={m.userId}>
                      {m.displayName} —{' '}
                      {keteranganTerakhir(state.data.terakhir.get(m.userId), sekarang)}
                    </option>
                  ))}
                </select>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      {gagal ? <ErrorCard error={gagal} /> : null}
    </>
  );
}
